import { ZERO_ROTATION, type CartesianPose } from '@sb/geometry';
import {
  clamp,
  clampByte,
} from '@sb/integrations/implementations/robotiq/shared/utils';

export { clamp, clampByte };

/**
 * The Robotiq 2F gripper has a "quasi-linear" relationship between grip width
 * and position byte value. We got poor accuracy trying to scale physical width
 * to certain byte values, as described in various Robotiq support articles/forums.
 * Instead, I sampled a few grip widths and their corresponding byte values.
 * and fit a least-squares regression to the data.
 *
 * width_mm = regressionSlope * byte + regressionIntercept
 *
 * Both slope and intercept are model-specific. The 2F-140 values are the original
 * empirical regression. The 2F-85 values are scaled by the ratio of max widths
 * (85/140) so that both variants use the same proportional byte range.
 */

export type Robotiq2FModelVariant = '2F-85' | '2F-140';

export type Robotiq2FVariantConfig = {
  minGripWidth: number;
  maxGripWidth: number;
  gripWidthTolerance: number;
  minSpeedMetersPerSecond: number;
  maxSpeedMetersPerSecond: number;
  minForceNewtons: number;
  maxForceNewtons: number;
  tcpZOffset: number;
  massKg: number;
  regressionSlope: number;
  regressionIntercept: number;
};

const VARIANT_CONFIG_MAP: Record<
  Robotiq2FModelVariant,
  Robotiq2FVariantConfig
> = {
  '2F-85': {
    minGripWidth: 0,
    maxGripWidth: 0.085,
    gripWidthTolerance: 0.00034,
    minSpeedMetersPerSecond: 0.02,
    maxSpeedMetersPerSecond: 0.15,
    minForceNewtons: 20,
    maxForceNewtons: 235,
    tcpZOffset: 0.174,
    massKg: 0.925,
    regressionSlope: -0.384463,
    regressionIntercept: 87.50771857,
  },
  '2F-140': {
    minGripWidth: 0,
    maxGripWidth: 0.14,
    gripWidthTolerance: 0.00055,
    minSpeedMetersPerSecond: 0.03,
    maxSpeedMetersPerSecond: 0.25,
    minForceNewtons: 10,
    maxForceNewtons: 125,
    tcpZOffset: 0.244,
    massKg: 1.025,
    regressionSlope: -0.633233,
    regressionIntercept: 144.13036,
  },
};

/** Default collision box for 2F-85 + 5 mm buffer. */
const ROBOTIQ_2F_85_DEFAULT_COLLISION_BOX_SIZE = {
  x: 0.1577,
  y: 0.08,
  z: 0.1543,
} as const;

export const ROBOTIQ_2F_85_DEFAULT_COLLISION_BOX = {
  origin: { x: 0, y: 0, z: ROBOTIQ_2F_85_DEFAULT_COLLISION_BOX_SIZE.z / 2 },
  size: ROBOTIQ_2F_85_DEFAULT_COLLISION_BOX_SIZE,
} as const;

/** Default collision box for 2F-140 + 5 mm buffer. */
const ROBOTIQ_2F_140_DEFAULT_COLLISION_BOX_SIZE = {
  x: 0.2119,
  y: 0.08,
  z: 0.2148,
} as const;

export const ROBOTIQ_2F_140_DEFAULT_COLLISION_BOX = {
  origin: { x: 0, y: 0, z: ROBOTIQ_2F_140_DEFAULT_COLLISION_BOX_SIZE.z / 2 },
  size: ROBOTIQ_2F_140_DEFAULT_COLLISION_BOX_SIZE,
} as const;

export function getRobotiq2FDefaultCollisionBox(
  variant: Robotiq2FModelVariant,
): {
  origin: { x: number; y: number; z: number };
  size: { x: number; y: number; z: number };
} {
  return variant === '2F-85'
    ? ROBOTIQ_2F_85_DEFAULT_COLLISION_BOX
    : ROBOTIQ_2F_140_DEFAULT_COLLISION_BOX;
}

export const getRobotiq2FVariantConfig = (
  variant: Robotiq2FModelVariant,
): Robotiq2FVariantConfig => {
  const config = VARIANT_CONFIG_MAP[variant];

  if (!config) {
    throw new Error(`Unsupported Robotiq 2F model variant: ${variant}`);
  }

  return config;
};

export const getEffectiveMinGripWidth = (fingerWidthMeters: number): number => {
  return 2 * fingerWidthMeters;
};

export const clampWidth = (
  width: number,
  config: Robotiq2FVariantConfig,
  fingerWidthMeters?: number,
) => {
  const minGripWidth = fingerWidthMeters
    ? getEffectiveMinGripWidth(fingerWidthMeters)
    : config.minGripWidth;

  return clamp(width, minGripWidth, config.maxGripWidth);
};

export const getRobotiq2FDefaultSpeedMetersPerSecond = (
  config: Robotiq2FVariantConfig,
): number => {
  return config.minSpeedMetersPerSecond + config.maxSpeedMetersPerSecond / 2;
};

export const getRobotiq2FDefaultForceNewtons = (
  config: Robotiq2FVariantConfig,
): number => {
  return config.minForceNewtons + config.maxForceNewtons / 2;
};

export const gripWidthMetersToByte = (
  width: number,
  config: Robotiq2FVariantConfig,
): number => {
  const widthMm = clamp(width, 0, config.maxGripWidth) * 1000;

  return clampByte(
    Math.round((widthMm - config.regressionIntercept) / config.regressionSlope),
  );
};

export const byteToGripWidthMeters = (
  byte: number,
  config: Robotiq2FVariantConfig,
): number => {
  const widthMm = config.regressionSlope * byte + config.regressionIntercept;

  return clamp(widthMm / 1000, 0, config.maxGripWidth);
};

export const speedMetersPerSecondToByte = (
  speed: number,
  config: Robotiq2FVariantConfig,
): number => {
  const clampedSpeed = clamp(
    speed,
    config.minSpeedMetersPerSecond,
    config.maxSpeedMetersPerSecond,
  );

  const normalized =
    (clampedSpeed - config.minSpeedMetersPerSecond) /
    (config.maxSpeedMetersPerSecond - config.minSpeedMetersPerSecond);

  return Math.round(normalized * 255);
};

export const forceNewtonsToByte = (
  force: number,
  config: Robotiq2FVariantConfig,
): number => {
  const clampedForce = clamp(
    force,
    config.minForceNewtons,
    config.maxForceNewtons,
  );

  const normalized =
    (clampedForce - config.minForceNewtons) /
    (config.maxForceNewtons - config.minForceNewtons);

  return Math.round(normalized * 255);
};

export const getDefaultTCPTransform = (
  variant: Robotiq2FModelVariant,
): CartesianPose => {
  return {
    ...ZERO_ROTATION,
    x: 0,
    y: 0,
    z: getRobotiq2FVariantConfig(variant).tcpZOffset,
  };
};
